/**
 * ConnectClaudeCard — Profile → "Connect your Claude" (SUN-85).
 * The URL to paste into the Claude desktop app, the person's live connections, and Disconnect.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { connectErrorText, connectorUrl, listClaudeConnections, revokeClaudeConnection, type ClaudeConnection } from './connectClaudeApi';

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—';

export function ConnectClaudeCard() {
  const [rows, setRows] = useState<ClaudeConnection[] | null>(null);
  const [url, setUrl] = useState(connectorUrl());
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    listClaudeConnections()
      .then((d) => { setRows(d.connections); setUrl(d.url); setError(null); })
      .catch((err) => { setRows([]); setError(connectErrorText(err)); });
  }, []);
  useEffect(load, [load]);

  const disconnect = async (id: string) => {
    setBusyId(id);
    try {
      await revokeClaudeConnection(id);
      load();
    } catch (err) {
      setError(connectErrorText(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="connect-claude-card">
      <div className="mb-2 flex items-center gap-2">
        <Bot className="h-5 w-5 text-gray-500" />
        <h3 className="text-lg font-semibold text-slate-900">Connect your Claude</h3>
      </div>
      <p className="text-sm text-gray-600">
        Let the Claude app on your computer work in AURA as you. In Claude: Settings → Connectors → Add custom connector → paste this URL.{' '}
        <Link to="/kiaan/connect-claude" className="underline">How it works</Link>
      </p>
      <div className="mt-3 flex items-center gap-2">
        <code className="break-all rounded bg-gray-100 px-2 py-1 text-xs">{url}</code>
        <button
          type="button"
          className="rounded border border-gray-200 px-2 py-1 text-xs"
          onClick={() => { void navigator.clipboard?.writeText(url).then(() => setCopied(true)).catch(() => undefined); }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {error && <div role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="mt-4">
        {rows === null && <p className="text-sm text-gray-500">Loading…</p>}
        {rows?.length === 0 && !error && <p className="text-sm text-gray-500">No Claude is connected.</p>}
        {rows && rows.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                <span>
                  <b>{r.name}</b>
                  <span className="ml-2 text-gray-500">connected {day(r.connectedAt)} · ends {day(r.expiresAt)}</span>
                </span>
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => void disconnect(r.id)}
                  className="rounded border border-red-200 px-2 py-1 text-xs text-red-700 disabled:opacity-50"
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
